import { IQueuedOperation, getOperationTypeKey, OperationTypeKey, editQueue, IOperationState } from '../reducers/operationMapReducer';
import { IItemCacheThunkAction, IItemCacheDispatch } from './ItemCacheActions';
import { IItemCacheState, getCacheItem } from '../reducers/itemCacheReducer';
import { Patch, ArrayConcatPatch, subtract } from '../../utilities/Graft';
import { IFacet, IFaceted, pack, Facet } from '../../models/Facet';
import { KeyFacet, Key } from '../../models/KeyFacet';
import { Signal } from '../../utilities/async/Signal';
import { preferredKeyFacetFacet } from '../../facets/OperationFacets';
import { IAsyncActionCreator, createAsyncActionCreator } from './createActionCreator';
import { waitForNextStoreUpdate } from '../enhancers/NextStoreUpdateEnhancer';
import { CancelError } from '../../utilities/async/CancelError';
import { waitForAll } from '../../utilities/async/all';
import { CustomError, ICustomErrorProps } from '@ms/utilities-error/lib/index';

/**
 * @internal
 */
export const STATUS_CODE_ZERO = 0;

/**
 * @public
 */
export interface IItemOperationErrorProps extends ICustomErrorProps {
  facets?: IFaceted;
}

/**
 * @public
 */
export class ItemOperationError extends CustomError {
  public readonly facets: IFaceted;

  constructor(props: IItemOperationErrorProps) {
    super(props);

    const {
      facets = pack({}, {})
    } = props;

    this.facets = facets;
  }
}

/**
 * @internal
 */
export const noRegisteredHandlerErrorFacet = new Facet<string>('noRegisteredHandlerError');

export {
  IQueuedOperation,
  getOperationTypeKey,
  OperationTypeKey,
  editQueue
};

/**
 * @public
 */
export interface IOperationPayload<TOptions> {
  items: {
    [itemKey: string]: TOptions;
  };
}

/**
 * @public
 */
export interface IOperationHandlerPayload<TKey extends object, TOptions extends object> {
  key: OperationTypeKey<TKey, TOptions>;
}

/**
 * Represents a single operator from a data source.
 * When invoked, this function initializes the data source and returns its 'updater' function,
 * which will be registered in the store.
 * @public
 */
export interface IOperationHandler<TKey extends object, TOptions extends object> {
  (payload: IOperationHandlerPayload<TKey, TOptions>): IItemCacheThunkAction<() => void>;
}

/**
 * Creates an action which, when dispatched, gets the current state of a specified
 * operation queue by its key.
 * @internal
 */
export function getOperationState<TKey extends object, TOptions extends object>(payload: {
  key: OperationTypeKey<TKey, TOptions>;
}): IItemCacheThunkAction<IOperationState<TKey, TOptions> | undefined> {
  return (dispatch: IItemCacheDispatch, getState: () => IItemCacheState) => {
    const {
      operationMap: {
        operations
      }
    } = getState();

    return operations[payload.key] as IOperationState<TKey, TOptions>;
  };
}

export type IEnqueueOperations<TKey extends object, TOptions extends object> = (
  currentOperations: IQueuedOperation<TKey, TOptions>[],
  newOperations: IQueuedOperation<TKey, TOptions>[]
) => IQueuedOperation<TKey, TOptions>[] | Patch<IQueuedOperation<TKey, TOptions>[]>;

/**
 * @public
 */
export interface IStartOperationsPayload<TOptions extends object> {
  operationTypeFacet: IFacet<TOptions>;
  items: {
    [itemKey: string]: TOptions;
  };
  enqueueOperations?: IEnqueueOperations<object, TOptions>;
}

/**
 * @public
 */
export interface IStartOperationsResult {
  started: Promise<void>;
  completed: Promise<void>;
  items: {
    [itemKey: string]: {
      started: Promise<void>;
      completed: Promise<void>;
    }
  };
}

/**
 * Creates an action which, when dispatched, queues an operation
 * of the given type for each input item key, with specified options.
 * Returns indicators for when individual operations start and complete,
 * as well as those for the overall scheduled batch.
 * @public
 */
export function startOperation<TOptions extends object>(
  payload: IStartOperationsPayload<TOptions>
): IItemCacheThunkAction<IStartOperationsResult> {
  const {
    items,
    enqueueOperations
  } = payload;

  return (dispatch: IItemCacheDispatch, getState: () => IItemCacheState) => {
    const state = getState();

    const queuesByKey: {
      [operationTypeKey: string]: IQueuedOperation<object, TOptions>[];
    } = {};

    const statesByKey: {
      [operationTypeKey: string]: IOperationState<object, TOptions> | undefined;
    } = {};

    const startedWork: Promise<void>[] = [];
    const completedWork: Promise<void>[] = [];

    const resultItems: {
      [itemKey: string]: {
        started: Promise<void>;
        completed: Promise<void>;
      };
    } = {};

    for (const itemKey in items) {
      if (itemKey) {
        if (Object.prototype.hasOwnProperty.call(items, itemKey)) {
          const {
            preferredItemKey,
            keyFacet
          } = getPreferredItemKey(state, itemKey);

          const operationTypeKey = getOperationTypeKey({
            operationTypeFacet: payload.operationTypeFacet,
            keyFacet: keyFacet
          });

          const operationState = (operationTypeKey in statesByKey) ?
            statesByKey[operationTypeKey] :
            (statesByKey[operationTypeKey] = dispatch(getOperationState({
              key: operationTypeKey
            })));

          if (operationState && operationState.operator) {
            let queue = queuesByKey[operationTypeKey];

            if (!queue) {
              queue = queuesByKey[operationTypeKey] = [];
            }

            const startSignal = new Signal<void>();
            const completeSignal = new Signal<void>();
            completeSignal.promise.then(startSignal.resolve, startSignal.reject);

            resultItems[itemKey] = {
              started: startSignal.promise,
              completed: completeSignal.promise
            };

            startedWork.push(startSignal.promise);
            completedWork.push(completeSignal.promise);

            queue.push({
              itemKey: <Key<object>>preferredItemKey,
              startSignal: startSignal,
              completeSignal: completeSignal,
              options: items[itemKey]
            });
          } else {
            // If there is no operation handler registered for the given type,
            // the Promise callbacks need to be invoked or else they will continue
            // to accumulate and leak memory.
            const started = Promise.resolve<void>(undefined);
            const completed = Promise.reject(new ItemOperationError({
              message: `There is no operation handler registered for '${operationTypeKey}'.`,
              facets: {
                ...noRegisteredHandlerErrorFacet.pack(operationTypeKey)
              }
            }));

            resultItems[itemKey] = {
              started: started,
              completed: completed
            };

            startedWork.push(started);
            completedWork.push(completed);
          }
        }
      }
    }

    for (const operationTypeKey of <OperationTypeKey<object, TOptions>[]>Object.keys(queuesByKey)) {
      const operationState = dispatch(getOperationState({
        key: operationTypeKey
      }));

      const queuePatchOperation = enqueueOperations ?
        enqueueOperations(operationState && operationState.queue || [], queuesByKey[operationTypeKey]) :
        new ArrayConcatPatch(queuesByKey[operationTypeKey]);

      dispatch(editQueue({
        key: operationTypeKey,
        queue: queuePatchOperation as (IQueuedOperation<object, object>[] | Patch<IQueuedOperation<object, object>[]>)
      }));

      // TODO Add some sort of wait time measurement for performance.
      dispatch(processOperation({
        key: operationTypeKey
      }));
    }

    return {
      started: Promise.race(startedWork).then(() => { /* Nothing */ }),
      completed: waitForAll(completedWork).then(() => { /* Nothing */ }),
      items: resultItems
    };
  };
}

/**
 * @public
 */
export interface ICancelOperationPayload<TOptions extends object> {
  operationTypeFacet: IFacet<TOptions>;
  items: {
    [itemKey: string]: TOptions;
  };
}

/**
 * Creator for actions which may be dispatched to 'clean up' a target queue,
 * removing all pending operations which are subsets of the given completed
 * batch, as well as reducing the scope of pending operations which intersect
 * with the completed batch.
 * @public
 */
export function cancelOperation<TOptions extends object>(
  payload: ICancelOperationPayload<TOptions>
): IItemCacheThunkAction<Promise<void>> {
  const {
    operationTypeFacet,
    items
  } = payload;

  return async (dispatch: IItemCacheDispatch, getState: () => IItemCacheState) => {
    const completeSignals: Signal<void>[] = [];

    const operationsByKey: {
      [key: string]: {
        items: {
          // Once the input keys are mapped to their preferred item keys,
          // multiple options might wind up bound to the same item key.
          [itemKey: string]: TOptions[];
        };
      };
    } = {};

    const state = getState();

    for (const itemKey in items) {
      if (Object.prototype.hasOwnProperty.call(items, itemKey)) {
        const {
          keyFacet,
          preferredItemKey
        } = getPreferredItemKey(state, itemKey);

        const operationTypeKey = getOperationTypeKey({
          operationTypeFacet: operationTypeFacet,
          keyFacet: keyFacet
        });

        const operations = operationsByKey[operationTypeKey] || (operationsByKey[operationTypeKey] = { items: {} });
        const options = operations.items[preferredItemKey] || (operations.items[preferredItemKey] = []);

        options.push(items[itemKey]);
      }
    }

    for (const operationTypeKey in operationsByKey) {
      if (Object.prototype.hasOwnProperty.call(operationsByKey, operationTypeKey)) {
        const key = operationTypeKey as OperationTypeKey<{}, TOptions>;

        const {
          completeSignals: nextCompleteSignals // tslint:disable-line:no-use-before-declare
        } = dispatch(cleanupOperationQueue({
          key: key,
          items: operationsByKey[key].items
        }));

        completeSignals.push(...nextCompleteSignals);
      }
    }

    await dispatch(waitForNextStoreUpdate());

    for (const completeSignal of completeSignals) {
      completeSignal.reject(new CancelError());
    }
  };
}

/**
 * Payload for an action to clean up a queue of matching operations.
 * @public
 */
export interface ICleanupOperationQueuePayload<TKey extends object, TOptions extends object> {
  key: OperationTypeKey<TKey, TOptions>;
  items: {
    [itemKey: string]: TOptions[];
  };
}

/**
 * Creates an action which, when dispatched, removes pending operations from the queue
 * which match the given item keys and operations.
 * @public
 */
export function cleanupOperationQueue<TKey extends object, TOptions extends object>(
  payload: ICleanupOperationQueuePayload<TKey, TOptions>
): IItemCacheThunkAction<{
  /**
   * The completion signals for all the redundant work removed when the given operation completed.
   */
  completeSignals: Signal<void>[];
}> {
  const {
    key,
    items
  } = payload;

  return (dispatch: IItemCacheDispatch) => {
    const completeSignals: Signal<void>[] = [];

    const operationState = dispatch(getOperationState({
      key: key
    }));

    if (operationState) {
      const {
        queue
      } = operationState;

      if (queue && queue.length) {
        let newQueue: (typeof queue) | undefined;

        for (let i = 0, length = queue.length; i < length; i++) {
          const operation = queue[i];

          const {
            itemKey
          } = operation;

          let remainingOptions = operation.options;

          const completedOptions = items[itemKey];

          if (completedOptions) {
            for (const options of completedOptions) {
              remainingOptions = subtract(remainingOptions, options);
            }
          }

          if (completedOptions && Object.keys(remainingOptions).length === 0) {
            // The executed operation supersedes the incoming operation.

            if (!newQueue) {
              // If the queue is now different, clone it up to this point and exclude this operation.
              newQueue = queue.slice(0, i);
            }

            completeSignals.push(operation.completeSignal);
          } else if (newQueue) {
            // If the queue has been altered, put this operation back on the updated queue.
            newQueue.push(operation);
          }
        }

        if (newQueue) {
          dispatch(editQueue({
            key: key,
            queue: newQueue
          }));
        }
      }
    }

    return {
      completeSignals: completeSignals
    };
  };
}

/**
 * Creates an action which, when dispatched, tells a registered operation runner that
 * its queue has changed and it needs to process a round of operations.
 * @internal
 */
export function processOperation<TKey extends object, TOptions extends object>(payload: {
  key: OperationTypeKey<TKey, TOptions>
}): IItemCacheThunkAction<void> {
  const {
    key
  } = payload;

  return (dispatch: IItemCacheDispatch) => {
    const operationState = dispatch(getOperationState({
      key: key
    }));

    if (operationState) {
      const {
        operator,
      } = operationState;

      if (operator) {
        operator();
      }
    }
  };
}

/**
 * @public
 */
export interface IOperationActionCreator<TOptions extends object> extends IAsyncActionCreator<
  IOperationPayload<TOptions>,
  IItemCacheThunkAction<Promise<void>>
  > {
  // Nothing.
}

/**
 * Creates an action creator to start operations of a given type.
 * @public
 */
export function implementOperation<TOptions extends object>(options: {
  operationTypeFacet: IFacet<TOptions>;
  enqueueOperations?: IEnqueueOperations<object, TOptions>;
}): IOperationActionCreator<TOptions> {
  const {
    operationTypeFacet,
    enqueueOperations
  } = options;

  return createAsyncActionCreator((payload: IOperationPayload<TOptions>): IItemCacheThunkAction<Promise<void>> =>
    async (dispatch: IItemCacheDispatch) => {
      await dispatch(startOperation({
        operationTypeFacet: operationTypeFacet,
        items: payload.items,
        enqueueOperations: enqueueOperations
      })).completed;
    });
}

/**
 * Gets the preferred item key from an item within the cache.
 * Returns the key facet and the mapped key.
 */
function getPreferredItemKey(state: IItemCacheState, itemKey: string): {
  preferredItemKey: string;
  keyFacet: KeyFacet<{}>;
} {
  const item = getCacheItem(state, itemKey);

  const preferredKeyFacet = item && preferredKeyFacetFacet.unpack(item);

  const preferredValue = item && preferredKeyFacet && preferredKeyFacet.unpack(item);

  const preferredItemKey = preferredValue && preferredKeyFacet && preferredKeyFacet.serialize(preferredValue) || itemKey;
  const keyFacet = preferredValue && preferredKeyFacet || KeyFacet.get(itemKey);

  return {
    preferredItemKey: preferredItemKey,
    keyFacet: keyFacet
  };
}
